import type { ReactNode } from "react";
import { Suspense, useEffect, useState } from "react";
import { useLocation, Link, matchPath, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { EmployeeBottomNav, EmployeeDesktopNav } from "@/components/layout/EmployeeBottomNav";
import { EmployeeSessionProvider } from "@/contexts/EmployeeSessionProvider";
import { PageContainer } from "@/components/layout/PageContainer";
import { AppShellNav } from "@/components/layout/AppShellNav";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/contexts/AuthContext";
import { getTemplate } from "@/lib/api/bundleTemplates";
import { getBundle } from "@/lib/api/quarterlyBundles";
import { BUNDLE_TITLE_CHANGED_EVENT, type BundleTitleChangedDetail } from "@/lib/bundleHelpers";
import { getVideo } from "@/lib/api/videos";
import { getEmployeeForUser, getOrganizationForUser } from "@/lib/orgAccess";
import { RoleAccessOutlet } from "@/components/auth/RoleAccessOutlet";
import { HeaderBackLink } from "@/components/ui/HeaderBackLink";
import { useEmployeeFunctionalCapacityGate } from "@/hooks/useEmployeeFunctionalCapacityGate";
import { useLinkedEmployee } from "@/hooks/useLinkedEmployee";
import { usePermissions } from "@/hooks/usePermissions";
import { adminListPathForContext } from "@/lib/adminOrgListContext";
import type { EmployeeMapsLocationState } from "@/types/employeeMapsNavigation";
import type { EmployeeRow } from "@/types/database";
import { RoutePageFallback } from "@/components/routing/RoutePageFallback";
import { lazyIntentHandlers, warmupCreateOrganization } from "@/routes/preloadChunks";

function getPageTitle(pathname: string, search = ""): string {
  if (pathname === "/admin") return "Dashboard";
  if (pathname === "/admin/leads") return "Leads";
  if (pathname.match(/^\/admin\/leads\/[^/]+$/)) return "";
  if (pathname === "/admin/clients") return "Organizations";
  if (pathname === "/admin/settings") return "Settings";
  if (pathname === "/admin/organizations/new") {
    return new URLSearchParams(search).get("status") === "client" ? "Add Client" : "Add Lead";
  }
  if (pathname.endsWith("/edit") && pathname.startsWith("/admin/organizations")) return "Edit organization";
  if (pathname.match(/^\/admin\/organizations\/[^/]+$/) && !pathname.endsWith("/edit")) return "";
  if (pathname === "/org" || pathname === "/org/") return "Dashboard";
  if (pathname === "/org/studio") return "Studio";
  if (pathname === "/org/employees") return "Employees";
  if (pathname === "/org/employees/import") return "Import employees";
  if (pathname.match(/^\/org\/employees\/[^/]+$/)) return "Employee";
  if (pathname === "/org/users/new") return "Create user";
  if (pathname === "/org/assessment") return "Take assessment";
  if (pathname.match(/^\/employee\/[^/]+\/maps$/)) return "Employee MAPS";
  if (pathname.match(/^\/employee\/[^/]+\/testing$/)) return "Testing";
  if (pathname === "/studio" || pathname === "/studio/") return "Studio";
  if (pathname === "/studio/videos") return "Videos";
  if (pathname.match(/^\/studio\/videos\/[^/]+$/)) return "Video";
  if (pathname === "/studio/exercises") return "Exercises";
  if (pathname === "/studio/templates") return "Templates";
  if (pathname === "/studio/templates/new") return "Create template";
  if (pathname.match(/^\/studio\/templates\/[^/]+$/)) return "Edit template";
  if (pathname === "/studio/self-care") return "Self Care";
  if (pathname.match(/^\/studio\/self-care\/[^/]+$/)) return "Edit Self Care region";
  if (pathname === "/studio/bundles") return "Bundles";
  if (pathname === "/studio/bundles/new") return "New bundle";
  if (pathname.match(/^\/studio\/bundles\/[^/]+\/edit$/)) return "Edit bundle";
  if (pathname.match(/^\/studio\/bundles\/[^/]+$/)) return "Bundle";
  if (pathname.match(/^\/studio\/player\/template\/[^/]+$/)) return "Studio player";
  if (pathname.match(/^\/studio\/player\/[^/]+$/)) return "Studio player";
  if (pathname === "/my" || pathname === "/my/") return "My program";
  if (pathname === "/my/self-care" || pathname.startsWith("/my/self-care/")) return "Self Care";
  if (pathname.match(/^\/my\/player\/template\/[^/]+$/)) return "My program";
  if (pathname === "/my/functional-capacity") return "Functional capacity";
  return "Fit2Lift";
}

function useStudioEntityHeaderTitle(pathname: string): string | null {
  const bundleEdit = matchPath({ path: "/studio/bundles/:id/edit", end: true }, pathname);
  const bundleDetail = matchPath({ path: "/studio/bundles/:id", end: true }, pathname);
  const videoDetail = matchPath({ path: "/studio/videos/:id", end: true }, pathname);
  const templateEdit = matchPath({ path: "/studio/templates/:id", end: true }, pathname);
  const templatePlayerPath = matchPath({ path: "/studio/player/template/:templateId", end: true }, pathname);
  const templatePlayerPathMy = matchPath({ path: "/my/player/template/:templateId", end: true }, pathname);

  const bundleId =
    bundleEdit?.params.id ??
    (bundleDetail?.params.id && bundleDetail.params.id !== "new" ? bundleDetail.params.id : undefined);
  const videoId = videoDetail?.params.id;
  const templateCatalogOrPlaybackId =
    (templatePlayerPath?.params.templateId && templatePlayerPath.params.templateId.trim()) ||
    (templatePlayerPathMy?.params.templateId && templatePlayerPathMy.params.templateId.trim()) ||
    (templateEdit?.params.id && templateEdit.params.id !== "new" ? templateEdit.params.id : undefined);

  const [entityTitle, setEntityTitle] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (bundleId) {
      void getBundle(bundleId).then((b) => {
        if (!cancelled && b?.title?.trim()) setEntityTitle(b.title.trim());
      });
      const onRenamed = (e: Event) => {
        const detail = (e as CustomEvent<BundleTitleChangedDetail>).detail;
        if (detail?.id === bundleId && detail.title.trim()) setEntityTitle(detail.title.trim());
      };
      window.addEventListener(BUNDLE_TITLE_CHANGED_EVENT, onRenamed);
      return () => {
        cancelled = true;
        window.removeEventListener(BUNDLE_TITLE_CHANGED_EVENT, onRenamed);
      };
    }

    if (videoId) {
      void getVideo(videoId).then((v) => {
        if (!cancelled && v?.title?.trim()) setEntityTitle(v.title.trim());
      });
      return () => {
        cancelled = true;
      };
    }

    if (templateCatalogOrPlaybackId) {
      void getTemplate(templateCatalogOrPlaybackId).then((t) => {
        if (!cancelled && t?.title?.trim()) setEntityTitle(t.title.trim());
      });
      return () => {
        cancelled = true;
      };
    }

    return () => {
      cancelled = true;
    };
  }, [pathname, bundleId, videoId, templateCatalogOrPlaybackId]);

  return entityTitle;
}

/** Org detail: full-width column with fixed horizontal inset (org sub-header is full viewport). */
function isAdminOrganizationDetailPage(pathname: string): boolean {
  if (!/^\/admin\/organizations\/[^/]+$/.test(pathname)) return false;
  return !pathname.endsWith("/new");
}

/**
 * The organisation form — added by hand or edited. It keeps its own max width but
 * takes the same even inset as the detail page, so the card sits the same distance
 * from the top as it does from the sides.
 */
function isAdminOrganizationFormPage(pathname: string): boolean {
  return (
    pathname === "/admin/organizations/new" ||
    /^\/admin\/organizations\/[^/]+\/edit$/.test(pathname)
  );
}

/** Leads, Clients, org Studio, employee Studio — same full-width main column as org console (e.g. MAPS), not the narrow `responsive` max. */
/** Org area: an organization and the employee pages opened from it (full-screen toggle in the top bar). */
function isOrgFullscreenArea(pathname: string): boolean {
  if (pathname === "/admin/organizations/new") return false;
  if (/^\/admin\/organizations\/[^/]+(\/.*)?$/.test(pathname)) return true;
  return /^\/employee\/[^/]+\/(testing|maps)$/.test(pathname);
}

function usesFullWidthMainShell(pathname: string): boolean {
  if (pathname === "/admin" || pathname === "/admin/") return true;
  if (pathname === "/admin/leads" || pathname === "/admin/clients") return true;
  if (pathname === "/admin/settings") return true;
  if (pathname.match(/^\/admin\/leads\/[^/]+$/)) return true;
  if (pathname === "/org" || pathname === "/org/") return true;
  if (pathname === "/my" || pathname === "/my/" || pathname.startsWith("/my/")) return true;
  if (pathname === "/org/studio") return true;
  if (pathname === "/studio" || pathname.startsWith("/studio/")) return true;
  return false;
}

function usesDashboardTightPadding(pathname: string): boolean {
  if (pathname === "/admin" || pathname === "/admin/") return true;
  if (pathname === "/admin/clients" || pathname === "/admin/leads") return true;
  if (pathname === "/org" || pathname === "/org/") return true;
  if (pathname === "/my" || pathname === "/my/" || pathname.startsWith("/my/")) return true;
  return false;
}

function getHeaderRight(pathname: string): ReactNode {
  /* Add Lead is not here any more: it sits on the Working tab, above the list it
     adds to. Only Add Client remains, because the Clients page has no such tab. */
  if (pathname === "/admin/clients") {
    return (
      <Link
        to="/admin/organizations/new?status=client"
        {...lazyIntentHandlers(warmupCreateOrganization)}
      >
        <Button className="text-sm min-h-[36px] py-1.5 px-3">Add Client</Button>
      </Link>
    );
  }
  return null;
}

/** Org detail page: render Back + org name in the sticky AppShell page header. */
function useOrganizationDetailHeader(pathname: string): {
  headerLeft: ReactNode;
  header: ReactNode;
} | null {
  const { user } = useAuth();
  const navigate = useNavigate();
  const match = matchPath({ path: "/admin/organizations/:id", end: true }, pathname);
  const id = match?.params.id;
  const onDetail = Boolean(match) && id !== "new";
  const [org, setOrg] = useState<{ name: string; org_status: string | null } | null>(null);

  useEffect(() => {
    if (!onDetail || !id) {
      setOrg(null);
      return;
    }
    let cancelled = false;
    getOrganizationForUser(user, id)
      .then((o) => {
        if (cancelled) return;
        setOrg(o ? { name: o.name, org_status: o.org_status } : null);
      })
      .catch(() => {
        if (!cancelled) setOrg(null);
      });
    return () => {
      cancelled = true;
    };
  }, [onDetail, id, user]);

  if (!onDetail) return null;

  const isOrgAdmin = user?.user_type === "orgadmin";
  const handleBack = () => {
    if (isOrgAdmin) {
      navigate("/org");
      return;
    }
    if (!org) {
      navigate(adminListPathForContext());
      return;
    }
    if (org.org_status === "sales") {
      navigate("/admin/leads");
      return;
    }
    navigate("/admin/clients");
  };

  return {
    headerLeft: <HeaderBackLink onClick={handleBack}>Back</HeaderBackLink>,
    header: (
      <span className="font-[var(--font-display)] text-xl font-bold tracking-tight text-[var(--color-text)] xl:text-2xl">
        {org?.name ?? ""}
      </span>
    ),
  };
}

function useEditOrganizationHeaderTitle(pathname: string): string | null {
  const { user } = useAuth();
  const match = matchPath({ path: "/admin/organizations/:id/edit", end: true }, pathname);
  const id = match?.params.id;
  const onEdit = Boolean(match);
  const [orgName, setOrgName] = useState<string | null>(null);

  useEffect(() => {
    if (!onEdit || !id) {
      setOrgName(null);
      return;
    }
    let cancelled = false;
    getOrganizationForUser(user, id)
      .then((org) => {
        if (!cancelled && org?.name) setOrgName(org.name);
        else if (!cancelled) setOrgName(null);
      })
      .catch(() => {
        if (!cancelled) setOrgName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [onEdit, id, user]);

  if (!onEdit) return null;
  return orgName ? `Edit ${orgName}` : "Edit organization";
}

function mapsIconButtonClass(): string {
  return (
    "flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)] border border-[var(--color-border-muted)] " +
    "text-[var(--color-steel-gray)] transition-all duration-200 hover:border-[var(--color-signal-blue)] " +
    "hover:bg-[var(--color-surface-elevated)] hover:text-[var(--color-text)] disabled:pointer-events-none disabled:opacity-30"
  );
}

function MapsChevron({ dir }: { dir: "left" | "right" }) {
  return (
    <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d={dir === "left" ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"}
      />
    </svg>
  );
}

/**
 * Employee MAPS: render "[name] Personal MAPS" plus Back / Edit / roster controls in the
 * app-shell top bar so the page body holds only the dashboard. Returns null off-route.
 */
function useEmployeeMapsHeader(
  pathname: string,
  opts: {
    linkedSelfId: string | null;
    linkedEmployee: EmployeeRow | null;
    fcComplete: boolean;
    fcCompleteSticky: boolean;
  }
): { headerLeft: ReactNode; header: ReactNode; headerRight: ReactNode } | null {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const match = matchPath({ path: "/employee/:employeeId/maps", end: true }, pathname);
  const id = match?.params.employeeId ?? "";
  const onMaps = Boolean(match) && Boolean(id);

  const state = (location.state ?? null) as EmployeeMapsLocationState | null;
  const returnTo = state?.returnTo;
  const scopedOrganizationId = state?.scopedOrganizationId;
  const roster = state?.roster;
  const isOwn = Boolean(id) && opts.linkedSelfId === id;

  const [fetchedName, setFetchedName] = useState<string | null>(null);

  useEffect(() => {
    if (!onMaps || !id) {
      setFetchedName(null);
      return;
    }
    if (isOwn && opts.linkedEmployee) {
      const n = `${opts.linkedEmployee.first_name} ${opts.linkedEmployee.last_name}`.trim();
      setFetchedName(n || null);
      return;
    }
    let cancelled = false;
    getEmployeeForUser(user, id, { scopedOrganizationId })
      .then((row) => {
        if (cancelled) return;
        setFetchedName(row ? `${row.first_name} ${row.last_name}`.trim() || null : null);
      })
      .catch(() => {
        if (!cancelled) setFetchedName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [onMaps, id, isOwn, opts.linkedEmployee, user, scopedOrganizationId]);

  if (!onMaps) return null;

  const rosterIndex = roster?.findIndex((r) => r.id === id) ?? -1;
  const nameFromRoster = rosterIndex >= 0 && roster ? roster[rosterIndex]?.name?.trim() : "";
  const displayName = fetchedName || nameFromRoster || "Team member";

  const showEdit =
    Boolean(scopedOrganizationId && id) && (!isOwn || opts.fcComplete || opts.fcCompleteSticky);
  const showRoster = Boolean(roster && roster.length > 1 && rosterIndex >= 0);

  const goBack = () => navigate(returnTo ?? adminListPathForContext());
  const goEdit = () =>
    navigate(`/admin/organizations/${scopedOrganizationId}/employees/${id}/edit?tab=assessment`, {
      state: { returnTo: `/employee/${id}/maps`, returnState: { roster, returnTo, scopedOrganizationId } },
    });
  const goSibling = (delta: number) => {
    if (!roster?.length || rosterIndex < 0) return;
    const next = rosterIndex + delta;
    if (next < 0 || next >= roster.length) return;
    navigate(`/employee/${roster[next].id}/maps`, {
      replace: true,
      state: { roster, returnTo, scopedOrganizationId },
    });
  };

  return {
    headerLeft: <HeaderBackLink onClick={goBack} />,
    header: (
      <span className="inline-flex max-w-full flex-wrap items-baseline justify-center gap-x-2 gap-y-0">
        <span className="truncate text-lg font-semibold text-[var(--color-text)]">{displayName}</span>
        <span className="shrink-0 font-[var(--font-display)] text-sm font-bold uppercase tracking-[0.12em] text-[var(--color-text-muted)]">
          Personal MAPS
        </span>
      </span>
    ),
    headerRight:
      showEdit || showRoster ? (
        <div className="flex items-center gap-2">
          {showEdit ? (
            <button
              type="button"
              onClick={goEdit}
              aria-label="Edit testing results"
              title="Edit testing results"
              className={mapsIconButtonClass()}
            >
              <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" aria-hidden>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 20h9M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z"
                />
              </svg>
            </button>
          ) : null}
          {showRoster ? (
            <>
              <button
                type="button"
                onClick={() => goSibling(-1)}
                disabled={rosterIndex <= 0}
                aria-label="Previous employee"
                className={mapsIconButtonClass()}
              >
                <MapsChevron dir="left" />
              </button>
              <button
                type="button"
                onClick={() => goSibling(1)}
                disabled={Boolean(roster) && rosterIndex >= (roster?.length ?? 0) - 1}
                aria-label="Next employee"
                className={mapsIconButtonClass()}
              >
                <MapsChevron dir="right" />
              </button>
            </>
          ) : null}
        </div>
      ) : null,
  };
}

/**
 * Employee edit (`/admin/organizations/:orgId/employees/:id/edit` and `/org/employees/:id/edit`):
 * show the employee name in the top bar with a left-aligned Back link, so the page body holds only
 * the Details/Assessment tabs. Honors a `returnTo` + `returnState` from navigation (e.g. coming from
 * Employee MAPS), otherwise falls back to the page's natural list. Null off-route.
 */
function useEmployeeEditHeader(
  pathname: string
): { headerLeft: ReactNode; header: ReactNode } | null {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const adminMatch = matchPath(
    { path: "/admin/organizations/:orgId/employees/:employeeId/edit", end: true },
    pathname
  );
  const orgMatch = matchPath({ path: "/org/employees/:id/edit", end: true }, pathname);
  const testingMatch = matchPath({ path: "/employee/:employeeId/testing", end: true }, pathname);
  const id = adminMatch?.params.employeeId ?? orgMatch?.params.id ?? testingMatch?.params.employeeId ?? "";
  const onEdit = Boolean(id);

  const state = (location.state ?? null) as { returnTo?: string; returnState?: unknown } | null;
  const returnTo = state?.returnTo;
  const returnState = state?.returnState;

  const [name, setName] = useState<string | null>(null);
  const [rowOrgId, setRowOrgId] = useState<string | null>(null);

  useEffect(() => {
    if (!onEdit || !id) {
      setName(null);
      setRowOrgId(null);
      return;
    }
    let cancelled = false;
    getEmployeeForUser(user, id)
      .then((row) => {
        if (cancelled) return;
        setName(row ? `${row.first_name} ${row.last_name}`.trim() || null : null);
        setRowOrgId(row?.org_id ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          setName(null);
          setRowOrgId(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [onEdit, id, user]);

  if (!onEdit) return null;

  // Testing: back to the org's Employees tab (org admins to their roster), like the page used to.
  const testingOrgId = (state as { scopedOrganizationId?: string } | null)?.scopedOrganizationId ?? rowOrgId;
  const defaultBack = adminMatch
    ? `/admin/organizations/${adminMatch.params.orgId}?tab=employees`
    : orgMatch
      ? "/org/employees"
      : user?.user_type === "orgadmin"
        ? "/org/employees"
        : testingOrgId
          ? `/admin/organizations/${testingOrgId}?tab=employees`
          : adminListPathForContext();

  const goBack = () =>
    navigate(returnTo ?? defaultBack, returnState ? { state: returnState } : undefined);

  return {
    headerLeft: <HeaderBackLink onClick={goBack} />,
    header: <span className="truncate text-lg font-semibold text-[var(--color-text)]">{name ?? ""}</span>,
  };
}

export function AuthenticatedLayout() {
  const { user } = useAuth();
  const { user_type, isEmployee } = usePermissions();
  const teamMemberEmployeeShell = user_type === "orguser" && isEmployee;
  const orgIdForFcGate = user?.org_id ?? null;

  if (teamMemberEmployeeShell) {
    return (
      <EmployeeSessionProvider enabled orgId={orgIdForFcGate}>
        <TeamMemberAuthenticatedLayout />
      </EmployeeSessionProvider>
    );
  }

  return <DefaultAuthenticatedLayout />;
}

function TeamMemberAuthenticatedLayout() {
  const location = useLocation();
  const { user } = useAuth();
  const { employeeId, linkedEmployee } = useLinkedEmployee();
  const {
    fcComplete,
    fcCompleteSticky,
    employeeId: gateEmployeeId,
  } = useEmployeeFunctionalCapacityGate(true, user?.org_id ?? null);

  const isEmployeeHome =
    location.pathname === "/org" || location.pathname === "/org/";

  return (
    <AuthenticatedLayoutShell
      layoutVariant="employee"
      employeeId={gateEmployeeId ?? employeeId}
      linkedEmployee={linkedEmployee}
      fcComplete={fcComplete}
      fcCompleteSticky={fcCompleteSticky}
      isEmployeeHome={isEmployeeHome}
      teamMemberEmployeeShell
    />
  );
}

function DefaultAuthenticatedLayout() {
  const { employeeId, linkedEmployee } = useLinkedEmployee();

  return (
    <AuthenticatedLayoutShell
      layoutVariant="default"
      employeeId={employeeId}
      linkedEmployee={linkedEmployee}
      fcComplete={false}
      fcCompleteSticky={false}
      isEmployeeHome={false}
      teamMemberEmployeeShell={false}
    />
  );
}

function AuthenticatedLayoutShell({
  layoutVariant,
  employeeId,
  linkedEmployee,
  fcComplete,
  fcCompleteSticky,
  isEmployeeHome,
  teamMemberEmployeeShell,
}: {
  layoutVariant: "default" | "employee";
  employeeId: string | null;
  linkedEmployee: EmployeeRow | null;
  fcComplete: boolean;
  fcCompleteSticky: boolean;
  isEmployeeHome: boolean;
  teamMemberEmployeeShell: boolean;
}) {
  const location = useLocation();

  const isEmployeeHomeResolved = isEmployeeHome;

  const employeeMapsHeader = useEmployeeMapsHeader(location.pathname, {
    linkedSelfId: employeeId,
    linkedEmployee,
    fcComplete,
    fcCompleteSticky,
  });
  const employeeEditHeader = useEmployeeEditHeader(location.pathname);

  const editTitle = useEditOrganizationHeaderTitle(location.pathname);
  const studioEntityTitle = useStudioEntityHeaderTitle(location.pathname);
  const orgDetailHeader = useOrganizationDetailHeader(location.pathname);
  const headerRight = getHeaderRight(location.pathname);

  const orgDetailBleed = isAdminOrganizationDetailPage(location.pathname);
  const orgFormPage = isAdminOrganizationFormPage(location.pathname);
  const fullWidthMainShell = usesFullWidthMainShell(location.pathname);
  const dashboardTightPadding = usesDashboardTightPadding(location.pathname);
  const isWideDashboard =
    location.pathname === "/maps-overview" ||
    /^\/my(\/.*)?$/.test(location.pathname) ||
    /^\/employee\/[^/]+\/(maps|testing)$/.test(location.pathname) ||
    /^\/org\/assessment(\/setup|\/start)?$/.test(location.pathname);
  const title: ReactNode = isEmployeeHomeResolved
    ? null
    : editTitle ??
      studioEntityTitle ??
      employeeMapsHeader?.header ??
      employeeEditHeader?.header ??
      orgDetailHeader?.header ??
      getPageTitle(location.pathname, location.search);

  const employeeNavProps = {
    employeeId,
    fcComplete,
    fcCompleteSticky,
  };

  return (
    <AppShell
      layoutVariant={layoutVariant}
      sidebar={teamMemberEmployeeShell ? undefined : <AppShellNav />}
      employeeDesktopNavigation={
        teamMemberEmployeeShell ? <EmployeeDesktopNav {...employeeNavProps} /> : undefined
      }
      bottomNavigation={
        teamMemberEmployeeShell ? <EmployeeBottomNav {...employeeNavProps} /> : undefined
      }
      header={title}
      headerLeft={employeeMapsHeader?.headerLeft ?? employeeEditHeader?.headerLeft ?? orgDetailHeader?.headerLeft}
      headerRight={employeeMapsHeader?.headerRight ?? headerRight}
      headerRightPersistent={Boolean(employeeMapsHeader?.headerRight)}
      fullscreenAvailable={isOrgFullscreenArea(location.pathname)}
    >
      <PageContainer
        maxWidth={orgDetailBleed || fullWidthMainShell ? "full" : isWideDashboard ? "2xl" : "responsive"}
        className={
          /* An even inset: the same space above the card as beside it. This was
             !py-0, which pinned the card against the header. */
          orgDetailBleed || orgFormPage
            ? "!p-4 sm:!p-6 md:!p-8"
            : location.pathname === "/admin/settings"
              ? /* Even 1.5rem inset; SettingsPage sizes its panel to fit beside it. */
                "!p-4 sm:!p-6"
              : dashboardTightPadding
                ? teamMemberEmployeeShell
                  ? "!px-3 !pb-8 !pt-8 sm:!px-5 sm:!pb-12 sm:!pt-9 md:!px-6" +
                    /* Employee home fits the window on desktop: fill the height, no scroll. */
                    (isEmployeeHomeResolved ? " lg:h-full lg:!py-6" : "")
                  : /* Even 1rem inset so the dashboard and lists fit the content area without a stray scroll. */
                    "!px-3 !py-4 sm:!px-5 md:!px-6"
                : ""
        }
      >
        <Suspense fallback={<RoutePageFallback variant="content" />}>
          <RoleAccessOutlet />
        </Suspense>
      </PageContainer>
    </AppShell>
  );
}
